import { type ReactNode } from "react";
import { cn } from "~/lib/utils";

// // // //
// Shared presentational primitives for legal documents (Privacy, Terms).
// Keeping these in one place guarantees the pages stay visually consistent.

export function LegalLayout(props: {
    title: string;
    /** e.g. "Last updated: June 5, 2023" */
    meta?: ReactNode;
    children: ReactNode;
}) {
    const { title, meta, children } = props;

    return (
        <div className="mx-auto max-w-3xl px-6 py-12 md:py-16">
            <header className="mb-10 border-b pb-8">
                <h1 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
                    {title}
                </h1>
                {meta ? (
                    <p className="mt-3 text-sm text-foreground">{meta}</p>
                ) : null}
            </header>
            <div className="space-y-10">{children}</div>
        </div>
    );
}

/**
 * Callout for the all-caps legal notices in the Terms (arbitration / waiver
 * blocks). Renders with a subtle border + background so they read as important
 * without shouting via raw markup.
 */
export function Notice(props: { children: ReactNode }) {
    return (
        <div
            role="note"
            className="rounded-lg border bg-muted/40 p-4 text-[13px] font-medium uppercase leading-relaxed tracking-wide text-foreground"
        >
            {props.children}
        </div>
    );
}

/** Main numbered section title used in the Terms document. */
export function SectionHeading(props: { children: ReactNode }) {
    return (
        <h2 className="mt-10 scroll-mt-nav-height text-xl font-semibold tracking-tight text-foreground first:mt-0">
            {props.children}
        </h2>
    );
}

/**
 * Typography wrapper for legal HTML migrated from rich-text sources. Styles
 * nested headings, paragraphs, lists, and links to match the design system.
 */
export function LegalProse(props: { children: ReactNode; className?: string }) {
    const { children, className } = props;

    return (
        <div
            className={cn(
                "space-y-4",
                "[&_h5]:mt-10 [&_h5:first-child]:mt-0 [&_h5]:text-xl [&_h5]:font-semibold [&_h5]:tracking-tight [&_h5]:text-foreground",
                "[&_h5_strong]:font-semibold",
                "[&_h6]:mt-6 [&_h6]:text-base [&_h6]:font-semibold [&_h6]:text-foreground",
                "[&_h6_strong]:font-semibold",
                "[&_p]:text-[15px] [&_p]:leading-relaxed [&_p]:text-foreground",
                "[&_p_strong]:font-semibold [&_p_strong]:text-foreground",
                "[&_ul]:list-outside [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_ul]:text-[15px] [&_ul]:leading-relaxed [&_ul]:text-foreground",
                "[&_li]:pl-1 [&_li_strong]:font-semibold [&_li_strong]:text-foreground",
                "[&_a]:text-link [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-link-hover",
                className,
            )}
        >
            {children}
        </div>
    );
}
